import { Box, Table } from "@mantine/core"
import { CaretRightIcon } from "@phosphor-icons/react"
import React, { useState } from "react"
import classes from "../analytics.module.css"
import { AnalyticsCard, EmptyRow } from "./AnalyticsCard"

export type RankColumn<T> = {
  key: string
  /** Header label */
  header: string
  /** Render cell content */
  render: (row: T, idx: number) => React.ReactNode
  /** Right-aligned? */
  align?: "left" | "right"
  /** Column width (CSS) */
  width?: string
}

type Props<T extends { breakdown?: unknown[] }> = {
  title: string
  subtitle?: string
  rows: T[] | undefined
  columns: RankColumn<T>[]
  isLoading: boolean
  /** Row id key (used for drill state) */
  rowKey: (row: T) => string
  /** Render drill content for a row */
  renderDrill?: (row: T) => React.ReactNode
  drillColSpan?: number
}

export function RankTable<T extends { breakdown?: unknown[] }>({
  title, subtitle, rows, columns, isLoading, rowKey, renderDrill, drillColSpan
}: Props<T>) {
  const [openId, setOpenId] = useState<string | null>(null)
  const colSpan = drillColSpan ?? columns.length

  const toggle = (id: string) => setOpenId(prev => prev === id ? null : id)

  return (
    <AnalyticsCard title={title} subtitle={subtitle} isLoading={isLoading}>
      <Box className={classes.tableWrap}>
        <Table className={classes.table} withRowBorders={false}>
          <Table.Thead>
            <Table.Tr>
              {columns.map(c => (
                <Table.Th key={c.key} w={c.width} data-align={c.align === "right" ? "right" : undefined}>
                  {c.header}
                </Table.Th>
              ))}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {(rows ?? []).map((row, idx) => {
              const id = rowKey(row)
              const isOpen = openId === id
              const canDrill = !!renderDrill && (row.breakdown?.length ?? 0) > 0
              const rowClass = [canDrill ? classes.rowDrill : "", isOpen ? classes.rowOpen : ""].join(" ").trim()
              return (
                <React.Fragment key={id}>
                  <Table.Tr className={rowClass || undefined} onClick={canDrill ? () => toggle(id) : undefined}>
                    {columns.map((c, ci) => (
                      <Table.Td key={c.key} data-align={c.align === "right" ? "right" : undefined}>
                        {ci === 1 && canDrill ? (
                          <Box component="span" className={classes.caret} data-open={isOpen || undefined}>
                            <CaretRightIcon size={12} weight="bold" />
                          </Box>
                        ) : null}
                        {c.render(row, idx)}
                      </Table.Td>
                    ))}
                  </Table.Tr>
                  {isOpen && renderDrill ? (
                    <Table.Tr className={classes.rowOpen}>
                      <Table.Td colSpan={colSpan}>
                        <Box className={classes.drill}>{renderDrill(row)}</Box>
                      </Table.Td>
                    </Table.Tr>
                  ) : null}
                </React.Fragment>
              )
            })}
          </Table.Tbody>
        </Table>
      </Box>
      {rows && rows.length === 0 && !isLoading ? <EmptyRow /> : null}
    </AnalyticsCard>
  )
}

/** Numbered badge (top 3 dark, as in the canvas Top 5 list) */
export function RankBadge({ n }: { n: number }) {
  return <Box component="span" className={classes.rank} data-top={n <= 3 || undefined}>{n}</Box>
}
